import React, { Component, ErrorInfo } from "react";
import { useArticles } from "../hooks/useArticles";
import { AccessTime } from "@mui/icons-material";
import Loader from "./Loader";
import { Button } from "./Button";
import { UIUtils } from "../utils/uiUtils";
import config from "../configs/config";
import guidesIcon from "../assets/icons/guides-icon.svg";

interface Article {
  id: number;
  title: string;
  authorName: string;
  authorImageUrl: string;
  articleImageUrl: string;
  summary: string;
  articleUrl: string;
  authorBioUrl: string;
  readTime: string;
  publishedTime: string;
  updatedTime: string;
}

interface ArticleItemCardProps {
  article: Article;
}

const authorImageMap: Record<string, string> = {
  "Chris Wheal": `${config.AFFORDA_STATIC_SITE}/images/authors/chris-wheal.png`,
  "Emma Lunn": `${config.AFFORDA_STATIC_SITE}/images/authors/emma-lunn.png`,
  "Rachel Wait": `${config.AFFORDA_STATIC_SITE}/images/authors/rachel-wait.png`,
  "Rebecca Goodman": `${config.AFFORDA_STATIC_SITE}/images/authors/rebecca-goodman.png`,
  "Harriet Meyer": `${config.AFFORDA_STATIC_SITE}/images/authors/harriet-meyer.png`,
  "Ruth Emery": `${config.AFFORDA_STATIC_SITE}/images/authors/ruth-emery.png`,
  "Adrian Holliday": `${config.AFFORDA_STATIC_SITE}/images/authors/adrian-holliday.png`,
  "Samantha Downes": `${config.AFFORDA_STATIC_SITE}/images/authors/samantha-downes.png`,
};

// Error boundary component to catch and handle errors
class ArticleErrorBoundary extends Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error) {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    UIUtils.trackException("ArticleList Error", { error, errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return null; // Silently fail as ArticleList is non-mandatory
    }

    return this.props.children;
  }
}

const ArticleItemCard: React.FC<ArticleItemCardProps> = ({ article }) => {
  const handleClick = () => {
    try {
      window.open(article.articleUrl, "_blank");
    } catch (error) {
      UIUtils.trackException("Article link error", error);
    }
  };

  // Safely access article properties with fallbacks
  const safeArticle = {
    title: article?.title || "Untitled Article",
    authorName: article?.authorName || "Unknown Author",
    readTime: article?.readTime || "Unknown read time",
    summary: article?.summary || "No summary available",
    articleUrl: article?.articleUrl || "#",
  };

  return (
    <div
      onClick={handleClick}
      className="container-white-rounded cursor-pointer"
    >
      <div className="flex flex-col">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <h3 className="text-xl font-bold mb-2">{safeArticle.title}</h3>
            <div className="flex items-center mb-2">
              <img
                src={
                  authorImageMap[safeArticle.authorName] ||
                  `${config.AFFORDA_STATIC_SITE}/images/authors/generic-avatar.png`
                }
                alt={safeArticle.authorName}
                className="w-8 h-8 rounded-full mr-2"
              />
              <span className="text-sm text-blue-600 font-semibold">
                by {safeArticle.authorName}
              </span>
            </div>
          </div>
          <div
            className="flex items-center"
            title="Estimated time to read this article"
          >
            <AccessTime className="w-4 h-4 mr-1" />
            <span className="text-sm">{safeArticle.readTime}</span>
          </div>
        </div>

        <p className="mb-2 line-clamp-3">{safeArticle.summary}</p>

        <button
          onClick={(e) => {
            e.stopPropagation();
            try {
              window.open(safeArticle.articleUrl, "_blank");
            } catch (error) {
              UIUtils.trackException("Article link error", error);
            }
          }}
          className="text-blue-600 underline text-sm text-left hover:text-blue-700"
        >
          Read more
        </button>
      </div>
    </div>
  );
};

interface ArticleListProps {
  userGoalStatement: string;
}

const ArticleListContent: React.FC<ArticleListProps> = ({
  userGoalStatement,
}) => {
  const { articles, loading, error } = useArticles(userGoalStatement);

  if (loading) {
    return <Loader />;
  }

  if (error || !Array.isArray(articles)) {
    return null;
  }

  if (articles.length === 0) {
    const handleViewAllGuides = () => {
      try {
        window.open(`${config.AFFORDA_STATIC_SITE}/guides/`, "_blank");
      } catch (error) {
        UIUtils.trackException("View all guides link error", error);
      }
    };

    return (
      <>
        <div className="flex mb-3">
          <img alt="guides icon" src={guidesIcon} />
          <h2 className="text-2xl ml-2.5">GUIDES</h2>
        </div>
        <div className="container-white-rounded text-center">
          <p className="mb-4">
            Oops! We couldn't find any guides related to your goal at the
            moment. Don't worry, our team is working hard to curate more
            relevant content for you. Stay tuned for updates!
          </p>
          <Button text="View all guides" onClick={handleViewAllGuides} />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="flex mb-3">
        <img alt="guides icon" src={guidesIcon} />
        <h2 className="text-2xl ml-2.5">GUIDES</h2>
      </div>
      <div className="space-y-4">
        {articles.map((article) => (
          <ArticleItemCard
            key={article?.id || Math.random()}
            article={article}
          />
        ))}
      </div>
    </>
  );
};

const ArticleList: React.FC<ArticleListProps> = (props) => (
  <ArticleErrorBoundary>
    <ArticleListContent {...props} />
  </ArticleErrorBoundary>
);

export default ArticleList;
